import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH, resetPasswordSchema } from '@mindpeer/auth-shared';
import { AuthLayout } from '@/components/auth-layout';
import { DarkAlert, DarkField, DarkInput, DarkSubmit, FieldIcons } from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { useZodForm } from '@/hooks/use-zod-form';
import { api } from '@/lib/api';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [done, setDone] = useState(false);

  const form = useZodForm({
    schema: resetPasswordSchema,
    initialValues: { token, password: '', passwordConfirmation: '' },
    onSubmit: async (input) => {
      await api.resetPassword(input);
      // The server revoked every session, including this browser's, so drop the
      // cached user rather than letting the UI look signed in (PRD §16).
      setUser(null);
      setDone(true);
    },
  });

  if (!token) {
    return (
      <AuthLayout
        title="This reset link is incomplete"
        description="The link you followed is missing its token. Request a new one to continue."
        footer={
          <Link to="/sign-in" className="font-medium text-brand-400 hover:text-brand-300">
            Back to sign in
          </Link>
        }
      >
        <DarkSubmit type="button" onClick={() => navigate('/forgot-password')}>
          Request a new link
        </DarkSubmit>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Password changed" description="Sign in with your new password.">
        <div className="space-y-5">
          <DarkAlert tone="success">
            For your security, we signed out every device that was using your old password.
          </DarkAlert>
          <DarkSubmit type="button" onClick={() => navigate('/sign-in', { replace: true })}>
            Sign in
          </DarkSubmit>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={form.handleSubmit} noValidate className="space-y-5">
        {form.formError && <DarkAlert tone="error">{form.formError}</DarkAlert>}

        <DarkField
          label="New password"
          htmlFor="field-password"
          error={form.errorFor('password')}
          hint={`At least ${PASSWORD_MIN_LENGTH} characters, including a number or symbol.`}
        >
          <DarkInput
            {...form.fieldProps('password')}
            type="password"
            icon={FieldIcons.lock}
            autoComplete="new-password"
            autoFocus
            required
            value={form.values.password}
            onChange={(event) => form.setValue('password', event.target.value)}
          />
        </DarkField>

        <DarkField
          label="Confirm new password"
          htmlFor="field-passwordConfirmation"
          error={form.errorFor('passwordConfirmation')}
        >
          <DarkInput
            {...form.fieldProps('passwordConfirmation')}
            type="password"
            icon={FieldIcons.lock}
            autoComplete="new-password"
            required
            value={form.values.passwordConfirmation}
            onChange={(event) => form.setValue('passwordConfirmation', event.target.value)}
          />
        </DarkField>

        <DarkSubmit loading={form.submitting}>
          Change password
        </DarkSubmit>
      </form>
    </AuthLayout>
  );
}
